<template>
  <Teleport to="body">
    <dialog ref="dialog" class="audio-versions" aria-labelledby="audio-versions-title" @cancel.prevent="close">
      <header class="audio-versions-head">
        <div><h2 id="audio-versions-title">Summary audio</h2><p>{{ authenticated ? 'Listen, compare, and choose a recording.' : 'Enter the collection password to manage recordings.' }}</p></div>
        <button type="button" class="audio-versions-close" aria-label="Close audio versions" @click="close"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg></button>
      </header>
      <form v-if="!authenticated" class="audio-versions-login" @submit.prevent="authenticate">
        <label for="audio-versions-password">Collection password</label>
        <input id="audio-versions-password" ref="password" v-model="password" type="password" autocomplete="current-password" :disabled="Boolean(busy)" required>
        <button type="submit" class="audio-versions-primary" :disabled="Boolean(busy) || !password">{{ busy ? 'Checking…' : 'Continue' }}</button>
      </form>
      <template v-else>
        <div v-if="versions.length" class="audio-versions-transport">
          <span>{{ previewVersion ? `Version ${previewVersion.number}` : 'Choose a recording' }}</span>
          <span class="audio-versions-time">{{ clock(currentTime) }} / {{ clock(duration) }}</span>
          <input type="range" min="0" :max="duration || 1" step="0.1" :value="currentTime" :disabled="!duration" :style="{ '--preview-progress': `${duration ? currentTime / duration * 100 : 0}%` }" aria-label="Preview playback position" :aria-valuetext="`${clock(currentTime)} of ${clock(duration)}`" @input="seek">
        </div>
        <ul class="audio-versions-list" aria-label="Summary recordings">
          <li v-for="version in versions" :key="version.id" :class="{ 'is-previewing': previewId === version.id, 'is-new': freshId === version.id }">
            <button type="button" class="audio-version-preview" :aria-label="`${previewId === version.id && playing ? 'Pause' : 'Play'} version ${version.number}`" :aria-pressed="previewId === version.id && playing" @click="playVersion(version)">
              <span class="audio-version-play" aria-hidden="true">
                <span v-if="previewId === version.id && previewLoading" class="audio-version-spinner"></span>
                <svg v-else-if="previewId === version.id && playing" viewBox="0 0 20 20"><path d="M7 5v10M13 5v10" /></svg>
                <svg v-else viewBox="0 0 20 20"><path class="audio-version-play-shape" d="m7 5 8 5-8 5Z" /></svg>
              </span>
              <span><strong>Version {{ version.number }}</strong><small>{{ versionDate(version) }}</small></span>
            </button>
            <span v-if="version.active" class="audio-version-in-use">In use</span>
            <button v-else type="button" class="audio-version-use" :disabled="Boolean(busy)" :aria-label="`Use version ${version.number}`" @click="useVersion(version)">{{ busy === `use-${version.id}` ? 'Saving…' : 'Use this' }}</button>
            <button type="button" class="audio-version-delete" :disabled="Boolean(busy) || version.active" :aria-label="`Delete version ${version.number}`" :title="version.active ? 'Choose another version before deleting this one.' : `Delete version ${version.number}`" @click="deleteId = version.id">
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 5.5h12M8 3h4l1 2.5H7L8 3ZM6 5.5l.7 11h6.6l.7-11M8.5 8v6M11.5 8v6" /></svg>
            </button>
            <div v-if="deleteId === version.id" class="audio-version-confirm">
              <span>Delete version {{ version.number }}?</span>
              <button type="button" :disabled="Boolean(busy)" @click="deleteId = ''">Keep</button>
              <button type="button" class="audio-version-delete-confirm" :disabled="Boolean(busy)" @click="deleteVersion(version)">{{ busy === `delete-${version.id}` ? 'Deleting…' : 'Delete' }}</button>
            </div>
          </li>
        </ul>
        <p v-if="!versions.length && !busy" class="audio-versions-empty">No recordings yet. Generate one to listen.</p>
        <footer class="audio-versions-foot">
          <p>{{ busy === 'generate' ? 'Recording a new version…' : 'New recordings stay here until you choose one.' }}</p>
          <button type="button" class="audio-versions-primary" :disabled="Boolean(busy)" @click="generate">{{ busy === 'generate' ? 'Generating…' : 'Generate new' }}</button>
        </footer>
      </template>
      <p v-if="error || previewError" class="audio-versions-error" role="alert">{{ error || previewError }}</p>
      <p v-if="notice || busy === 'load'" class="audio-versions-notice" role="status">{{ busy === 'load' ? 'Loading recordings…' : notice }}</p>
      <audio ref="preview" :src="previewSrc || undefined" preload="none" @loadedmetadata="loaded" @timeupdate="updateTime" @durationchange="updateTime" @play="onPlay" @pause="onPause" @ended="onPause" @error="onPlaybackError" />
    </dialog>
  </Teleport>
</template>

<script>
import { setSpeechPlaybackRate } from '../../utils/speechPlayback';
import { hasSharedViewsAdminSession, clearSharedViewsAdminSession, loginSharedViewsAdmin,
  listManagedViewAudio, regenerateManagedViewAudio, selectManagedViewAudio, deleteManagedViewAudio } from '../../utils/viewShareSite';
import { initDialogBackdrop } from '../../utils/dialogBackdrop';

export default {
  name: 'ViewAudioVersions',
  props: { shareId: { type: String, required: true } },
  emits: ['close', 'audio-selected'],
  data: () => ({ authenticated: false, password: '', busy: '', error: '', notice: '', versions: [], currentAudio: null,
    previewId: '', previewSrc: '', previewLoading: false, previewError: '', playing: false, currentTime: 0, duration: 0,
    freshId: '', deleteId: '' }),
  computed: { previewVersion() { return this.versions.find(version => version.id === this.previewId); } },
  mounted() {
    this._abort = new AbortController();
    this.$refs.dialog.showModal();
    this._stopBackdrop = initDialogBackdrop(this.$refs.dialog, () => this.close());
    this._otherAudio = event => { if (event.detail !== this) this.$refs.preview?.pause(); };
    window.addEventListener('view-summary-play', this._otherAudio);
    if (hasSharedViewsAdminSession()) this.load();
    else this.$refs.password?.focus();
  },
  beforeUnmount() {
    this._closed = true;
    this._abort?.abort();
    this._stopBackdrop?.();
    this.$refs.preview?.pause();
    this.$refs.dialog?.close();
    window.removeEventListener('view-summary-play', this._otherAudio);
  },
  methods: {
    close() { this._closed = true; this._abort?.abort(); this.$emit('close'); },
    async action(name, work) {
      if (this.busy) return;
      this.busy = name; this.error = ''; this.notice = '';
      try { await work(this._abort.signal); }
      catch (error) {
        if (this._closed) return;
        const message = error?.message;
        if (['forbidden', 'invalid_credentials'].includes(message)) {
          clearSharedViewsAdminSession(); this.resetPreview(); this.versions = []; this.currentAudio = null; this.authenticated = false; this.password = '';
          this.$nextTick(() => this.$refs.password?.focus());
        }
        this.error = message === 'invalid_credentials' ? 'That password did not work.'
          : message === 'forbidden' ? 'Enter the collection password to continue.'
            : message === 'too_many_attempts' ? 'Please wait a minute before trying again.'
              : message === 'version_in_use' ? 'Choose another recording before deleting this one.'
                : message === 'conflict' ? 'The selected recording changed. Review the list and try again.'
                  : 'The recording could not be updated. Please try again.';
        if (['conflict', 'version_in_use', 'version_not_found', 'version_deleted'].includes(message)) {
          try { this.apply(await listManagedViewAudio(this.shareId, this._abort.signal)); } catch { /* Keep the original actionable error. */ }
        }
      } finally { if (!this._closed) this.busy = ''; }
    },
    apply(data) {
      if (this._closed) return;
      this.authenticated = true; this.password = '';
      this.versions = data.versions; this.currentAudio = data.current_audio;
      if (this.previewId && !this.versions.some(version => version.id === this.previewId)) this.resetPreview();
    },
    load() { return this.action('load', async signal => this.apply(await listManagedViewAudio(this.shareId, signal))); },
    authenticate() {
      if (!this.password) return;
      return this.action('auth', async signal => {
        await loginSharedViewsAdmin(this.password, signal);
        this.apply(await listManagedViewAudio(this.shareId, signal));
      });
    },
    generate() {
      return this.action('generate', async signal => {
        this._generationId ||= crypto.randomUUID();
        const controller = new AbortController();
        const cancel = () => controller.abort();
        signal.addEventListener('abort', cancel, { once: true });
        const timeout = setTimeout(cancel, 120000);
        let audio;
        try { audio = await regenerateManagedViewAudio(this.shareId, this._generationId, controller.signal); }
        catch (error) {
          if (['unavailable', 'audio_unavailable', 'conflict', 'invalid_summary', 'not_found', 'version_deleted'].includes(error?.message)) this._generationId = null;
          throw error;
        } finally { clearTimeout(timeout); signal.removeEventListener('abort', cancel); }
        this._generationId = null;
        this.apply(await listManagedViewAudio(this.shareId, signal));
        if (this._closed) return;
        this.freshId = this.versions.find(version => version.audio.src === audio.src)?.id || '';
        this.notice = 'New recording ready to compare.';
      });
    },
    useVersion(version) {
      return this.action(`use-${version.id}`, async signal => {
        const data = await selectManagedViewAudio(this.shareId, version.id, this.currentAudio?.src || '', signal);
        if (this._closed) return;
        this.apply(data); this.$emit('audio-selected', data.current_audio);
        this.notice = `Version ${version.number} is now in use.`;
      });
    },
    deleteVersion(version) {
      return this.action(`delete-${version.id}`, async signal => {
        this.apply(await deleteManagedViewAudio(this.shareId, version.id, signal));
        if (this._closed) return;
        this.deleteId = ''; this.notice = `Version ${version.number} deleted.`;
      });
    },
    async playVersion(version) {
      const audio = this.$refs.preview;
      if (this.previewId === version.id && (this.playing || this.previewLoading)) { this._playId = (this._playId || 0) + 1; audio.pause(); this.previewLoading = false; return; }
      this.previewError = '';
      const playId = this._playId = (this._playId || 0) + 1;
      if (this.previewId !== version.id) {
        this._seekAfterLoad = this.currentTime;
        audio.pause(); this.previewId = version.id; this.previewSrc = version.audio.src;
        this.previewLoading = true; this.duration = 0;
        await this.$nextTick();
      } else if (audio.ended) audio.currentTime = 0;
      if (this._closed || this._playId !== playId) return;
      this.previewLoading = true;
      setSpeechPlaybackRate(audio);
      try { await audio.play(); }
      catch (error) {
        if (this._closed || this._playId !== playId || error.name === 'AbortError') return;
        this.previewError = 'Tap play to listen to this recording.'; this.previewLoading = false;
      }
    },
    loaded() {
      const audio = this.$refs.preview;
      if (Number.isFinite(this._seekAfterLoad) && Number.isFinite(audio.duration)) {
        audio.currentTime = this._seekAfterLoad < audio.duration ? this._seekAfterLoad : 0;
        this._seekAfterLoad = null;
      }
      this.updateTime();
    },
    onPlay() {
      this.playing = true; this.previewLoading = false;
      window.dispatchEvent(new CustomEvent('view-summary-play', { detail: this }));
    },
    onPause() { this.playing = false; window.dispatchEvent(new CustomEvent('view-summary-pause', { detail: this })); },
    onPlaybackError() { if (this.previewSrc) { this.previewLoading = false; this.previewError = 'Audio is unavailable. Try another recording.'; } },
    resetPreview() { this._playId = (this._playId || 0) + 1; this.$refs.preview?.pause(); this.previewId = ''; this.previewSrc = ''; this.currentTime = 0; this.duration = 0; this.previewLoading = false; this.previewError = ''; this.playing = false; this._seekAfterLoad = null; },
    updateTime() { const audio = this.$refs.preview; this.currentTime = audio?.currentTime || 0; this.duration = Number.isFinite(audio?.duration) ? audio.duration : 0; },
    seek(event) { this.$refs.preview.currentTime = Number(event.target.value); this.updateTime(); },
    clock(seconds) { const n = Math.floor(seconds || 0); return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`; },
    versionDate(version) { return version.created_at ? new Date(version.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Existing recording'; },
  },
};
</script>

<style scoped>
.audio-versions { box-sizing: border-box; width: min(600px, calc(100vw - 32px)); max-height: calc(100dvh - 40px); padding: 24px; border: 1px solid color-mix(in srgb, var(--site-text-primary, #333) 12%, transparent); border-radius: 14px; background: var(--site-bg, #fff); color: var(--site-text-primary, #333); box-shadow: 0 24px 80px #0003; font: 13px/1.5 -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif; cursor: var(--site-default-cursor); }
.audio-versions::backdrop { background: #0005; backdrop-filter: blur(5px); }
.audio-versions-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 20px; }
.audio-versions h2 { margin: 0; color: inherit; font: 600 20px/1.3 -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif; letter-spacing: -.02em; }
.audio-versions-head p { margin: 6px 0 0; color: var(--site-text-secondary, #666); font: inherit; }
.audio-versions button { appearance: none; min-height: 40px; padding: 8px 10px; border: 0; border-radius: 6px; background: none; color: inherit; font: inherit; cursor: var(--site-interactive-cursor); transition: background-color 140ms ease, color 140ms ease; }
.audio-versions button:disabled { opacity: .35; cursor: var(--site-default-cursor); }
.audio-versions button:focus-visible, .audio-versions input:focus-visible { outline: 2px solid var(--site-accent, #505050); outline-offset: 2px; }
.audio-versions button:not(:disabled):active { transform: scale(.98); }
.audio-versions svg { display: block; width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.3; stroke-linecap: round; stroke-linejoin: round; }
.audio-versions .audio-versions-close { flex: 0 0 40px; margin: -8px -8px 0 0; padding: 10px; }
.audio-versions-login { display: grid; gap: 10px; }
.audio-versions-login label { font-size: 12px; }
.audio-versions-login input { box-sizing: border-box; width: 100%; min-height: 44px; padding: 9px 12px; border: 1px solid color-mix(in srgb, currentColor 24%, transparent); border-radius: 6px; background: transparent; color: inherit; font: inherit; }
.audio-versions .audio-versions-primary { padding-inline: 14px; background: var(--site-text-primary, #333); color: var(--site-bg, #fff); white-space: nowrap; }
.audio-versions-login .audio-versions-primary { justify-self: end; margin-top: 8px; }
.audio-versions-transport { display: grid; grid-template-columns: 1fr auto; gap: 10px; padding: 0 0 18px; color: var(--site-text-secondary, #666); font-size: 12px; }
.audio-versions-time { font-variant-numeric: tabular-nums; }
.audio-versions-transport input { grid-column: 1 / -1; width: 100%; margin: 0; accent-color: var(--site-text-primary, #333); cursor: var(--site-interactive-cursor); }
.audio-versions-transport input { appearance: none; height: 18px; padding: 0; border: 0; background: transparent; }
.audio-versions-transport input::-webkit-slider-runnable-track { height: 2px; border-radius: 2px; background: linear-gradient(to right, var(--site-text-primary, #333) var(--preview-progress), color-mix(in srgb, var(--site-text-primary, #333) 12%, transparent) var(--preview-progress)); }
.audio-versions-transport input::-moz-range-track { height: 2px; border-radius: 2px; background: color-mix(in srgb, var(--site-text-primary, #333) 12%, transparent); }
.audio-versions-transport input::-moz-range-progress { height: 2px; background: var(--site-text-primary, #333); }
.audio-versions-transport input::-webkit-slider-thumb { appearance: none; width: 8px; height: 8px; margin-top: -3px; border: 0; border-radius: 50%; background: var(--site-text-primary, #333); }
.audio-versions-transport input::-moz-range-thumb { width: 8px; height: 8px; border: 0; border-radius: 50%; background: var(--site-text-primary, #333); }
.audio-versions-transport input:disabled { opacity: .35; cursor: var(--site-default-cursor); }
.audio-versions-list { max-height: min(360px, 44dvh); overflow-y: auto; list-style: none; margin: 0; padding: 0; overscroll-behavior: contain; }
.audio-versions-list li { display: grid; grid-template-columns: minmax(0, 1fr) 76px 40px; align-items: center; column-gap: 6px; padding: 5px 0; border-top: 1px solid color-mix(in srgb, currentColor 10%, transparent); }
.audio-versions .audio-version-preview { display: grid; grid-template-columns: 28px minmax(0, 1fr); align-items: center; gap: 7px; padding: 8px 4px; text-align: left; }
.audio-version-preview strong, .audio-version-preview small { display: block; }
.audio-version-preview strong { font-size: 13px; font-weight: 500; }
.audio-version-preview small { margin-top: 3px; color: var(--site-text-secondary, #666); font-size: 11px; }
.audio-version-play { display: grid; place-items: center; width: 28px; height: 28px; }
.audio-version-play-shape { fill: currentColor; stroke: none; }
.is-previewing .audio-version-play { color: var(--site-accent, #505050); }
.is-new .audio-version-preview strong::after { content: ''; display: inline-block; width: 4px; height: 4px; margin: 0 0 2px 7px; border-radius: 50%; background: var(--site-accent, #505050); }
.audio-version-in-use { text-align: center; font-size: 11px; color: var(--site-accent, #505050); }
.audio-versions .audio-version-use { font-size: 12px; }
.audio-versions .audio-version-delete { padding: 10px; color: var(--site-text-secondary, #666); }
.audio-version-confirm { display: flex; grid-column: 1 / -1; align-items: center; justify-content: flex-end; gap: 6px; padding: 5px 0 3px; }
.audio-version-confirm > span { margin-right: auto; font-size: 12px; }
.audio-versions .audio-version-delete-confirm { color: #a64137; }
.audio-versions-foot { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding-top: 18px; border-top: 1px solid color-mix(in srgb, currentColor 10%, transparent); }
.audio-versions-foot p { max-width: 270px; margin: 0; font-size: 12px; line-height: 1.5; color: var(--site-text-secondary, #666); }
.audio-versions-error, .audio-versions-notice, .audio-versions-empty { margin: 14px 0 0; color: var(--site-text-secondary, #666); font: inherit; }
.audio-versions-empty { margin-bottom: 20px; }
.audio-versions-error { color: var(--site-text-primary, #333); }
.audio-version-spinner { width: 12px; height: 12px; border: 1px solid currentColor; border-right-color: transparent; border-radius: 50%; animation: audio-version-spin 600ms linear infinite; }
@keyframes audio-version-spin { to { transform: rotate(360deg); } }
:global(body.dark-mode .audio-versions) { --site-bg: #242425; --site-text-primary: #e6e2dc; --site-text-secondary: #b4b1ad; }
@media (hover: hover) and (pointer: fine) { .audio-versions button:hover:not(:disabled) { background-color: color-mix(in srgb, currentColor 7%, transparent); } .audio-versions .audio-versions-primary:hover:not(:disabled) { background-color: color-mix(in srgb, var(--site-text-primary, #333) 85%, var(--site-bg, #fff)); } }
@media (max-width: 480px) { .audio-versions { padding: 20px 16px; } .audio-versions-foot { gap: 12px; } .audio-versions-foot p { font-size: 11px; } }
@media (prefers-reduced-motion: reduce) { .audio-versions button { transition: none; } .audio-version-spinner { animation: none; } }
</style>
